import { Fragment, useContext, useMemo } from "react";
import useTicketManagers from "../../useTicketManagers/useTicketManagers";
import { useWatch, UseFormReturn } from "react-hook-form";
import {
  getKeysDisplayForMapping,
  getKeysThatHoldsValue,
  ticketEndpointContextEnum,
  ticketEndpointTypeEnum,
} from "../../TicketingUtils";
import useManualTicket from "../../useManualTicket";
import { useApolloClient } from "@apollo/client";
import {
  TextButton,
  AlertIndicator,
  alertTypes,
  ModalContext,
  type ModalContextType,
} from "@SeemplicityDev/shared";
import TicketEndpointColumns, {
  ticketEndpointColumnsModesEnum,
  type TicketEndpointColumnsMode,
} from "../../TicketEndpointColumns/TicketEndpointColumns";
import "./MappingAlert.scss";

export interface MappingAlertProps {
  mode: TicketEndpointColumnsMode;
  getPropsFromField: (fieldName: string) => object;
  wizardAnalyticsProps?: string;
  onAlertClick?: (mode: TicketEndpointColumnsMode) => void;
  selectedIntegrationType: string;
  formMethods: UseFormReturn<any>;
}

const MappingAlert: React.FC<MappingAlertProps> = ({
  mode,
  getPropsFromField,
  wizardAnalyticsProps,
  onAlertClick,
  selectedIntegrationType,
  formMethods,
}) => {
  const client = useApolloClient();
  const { openModal, closeModal } = useContext(
    ModalContext
  ) as ModalContextType;
  const { onCreateMapping, onCreateMappingError } = useManualTicket();
  const { getTicketManagerProps } = useTicketManagers({
    context: ticketEndpointContextEnum.RMQ,
    type: ticketEndpointTypeEnum.TICKET,
  });

  const { control, watch } = formMethods;

  const selectedKeys = watch("keys", {}) as Record<string, string>;
  const selectedTicketProvider = useWatch({
    control,
    name: "ticketProvider",
  });

  const filledKeys = getKeysThatHoldsValue(selectedKeys);

  const ticketManagerProps = useMemo(() => {
    if (!!selectedIntegrationType && !!getTicketManagerProps) {
      return getTicketManagerProps(selectedIntegrationType);
    }
  }, [getTicketManagerProps, selectedIntegrationType]);

  const createMappingText = useMemo(() => {
    if (!!ticketManagerProps) {
      return `No Mapping configured${
        !ticketManagerProps?.mustHaveMapping ? " (Optional)" : "."
      }`;
    }
  }, [ticketManagerProps]);

  const editMappingText = useMemo(() => {
    if (!filledKeys) {
      return;
    }

    if (!!filledKeys && !!getPropsFromField) {
      const filledKeysDisplayNames = getKeysDisplayForMapping(
        filledKeys,
        getPropsFromField
      );

      return (
        <span>
          {`There is an existing Field Mapping for `}
          {!!filledKeysDisplayNames?.length ? (
            filledKeysDisplayNames?.map(
              (keyDisplay: string | undefined, index: number) => (
              <Fragment key={`${keyDisplay}-${index}`}>
                <span className={"modal-live-text"}>{keyDisplay}</span>
                {index !== filledKeysDisplayNames?.length - 1 && " and "}
              </Fragment>
            ))
          ) : (
            <span className={"modal-live-text"}>
              {ticketManagerProps?.friendlyName}
            </span>
          )}
          {`.`}
        </span>
      );
    }
  }, [ticketManagerProps, getPropsFromField, filledKeys]);

  const alertData = useMemo(() => {
    if (mode === ticketEndpointColumnsModesEnum.CREATE) {
      return {
        type: alertTypes.GENERAL,
        text: createMappingText,
        label: "Click to configure",
        modalTitle: "Create Mapping",
      };
    }

    return {
      type: alertTypes.SUCCESS,
      text: editMappingText,
      label: "Edit Mapping",
      modalTitle: "Edit Mapping",
    };
  }, [mode, createMappingText, editMappingText]);

  return (
    <AlertIndicator
      type={alertData.type}
      content={
        <div className={"alert-indicator-content-text-and-button-wrap"}>
          <div>{alertData.text}</div>
          <TextButton
            data-testid={"mapping-indicator-action"}
            type={"button"}
            label={alertData.label}
            buttonStyle={"textLink"}
            {...(!!wizardAnalyticsProps
              ? {
                  "data-analytics-event": "user_click",
                  "data-analytics-props": `{"name": "banner_edit_mapping_btn"${wizardAnalyticsProps}}`,
                }
              : {})}
            onClick={() => {
              onAlertClick?.(mode);
              openModal({
                title: alertData?.modalTitle || "",
                size: "m",
                overlay: true,
                component: (
                  <TicketEndpointColumns
                    ticketProviderId={selectedTicketProvider}
                    keysObject={filledKeys}
                    ticketManagerId={selectedIntegrationType}
                    getPropsFromKeyField={getPropsFromField}
                    mode={mode}
                    ticketManagerMustHaveColumnsMapping={
                      ticketManagerProps?.mustHaveColumnsMapping
                    }
                    onClose={closeModal}
                    onCreateMapping={onCreateMapping}
                    onCreateMappingError={onCreateMappingError}
                    client={client}
                  />
                ),
              });
            }}
          />
        </div>
      }
      hasMaxWidth={false}
      wrapperClassName={"mapping-alert"}
    />
  );
};

export default MappingAlert;
